/**
 * Navigationseinträge der Seitenleiste als reine Daten. Reihenfolge und Auswahl hängen vom
 * Startprofil ab (src/lib/workspaceProfile.ts). Für das Standalone-Angebotsprodukt ändert ein
 * neuer Entwurf nur diese Datei, das Profil und die Angebots-Übersicht.
 */
import type { WorkspaceProfile } from '../../lib/workspaceProfile';

export type NavItemKey =
  | 'dashboard'
  | 'new_offer'
  | 'chat'
  | 'inbox'
  | 'calendar'
  | 'aufgaben'
  | 'leads'
  | 'invoices'
  | 'contacts'
  | 'stammdaten'
  | 'ausschreibungen'
  | 'templates'
  | 'employees'
  | 'fuhrpark';

export type NavItem = {
  key: NavItemKey;
  label: string;
  /** Pfad nach /i/:instanceId. */
  path: string;
  /** Recht, ohne das der Eintrag fehlt (Rechte abgewählter Module sind schon gestrichen). */
  capability?: string;
  /** Erscheint im „Mehr“-Menü auf dem Handy (Übersicht, Projekte, Posteingang, Kontakte stehen in der unteren Leiste). */
  inMobileDrawer: boolean;
};

const ITEMS: Record<NavItemKey, NavItem> = {
  dashboard: { key: 'dashboard', label: 'Übersicht', path: '/dashboard', inMobileDrawer: false },
  new_offer: {
    key: 'new_offer',
    label: 'Neues Angebot',
    path: '/projects/list?neuesAngebot=1',
    capability: 'web.projekte.edit',
    inMobileDrawer: true,
  },
  chat: { key: 'chat', label: 'KI Chat', path: '/chat', capability: 'web.kichat.view', inMobileDrawer: true },
  inbox: { key: 'inbox', label: 'Posteingang', path: '/inbox', capability: 'web.posteingang.view', inMobileDrawer: false },
  calendar: { key: 'calendar', label: 'Plantafel', path: '/calendar/calendar', capability: 'web.plantafel.view', inMobileDrawer: true },
  aufgaben: { key: 'aufgaben', label: 'To-dos', path: '/todos', capability: 'web.aufgaben.own', inMobileDrawer: true },
  leads: { key: 'leads', label: 'Projekte', path: '/projects', capability: 'web.projekte.view', inMobileDrawer: false },
  invoices: { key: 'invoices', label: 'Rechnungen', path: '/invoices/outgoing', capability: 'web.rechnungen.view', inMobileDrawer: true },
  contacts: { key: 'contacts', label: 'Kontakte', path: '/contacts', capability: 'web.kontakte.view', inMobileDrawer: false },
  stammdaten: {
    key: 'stammdaten',
    label: 'Stammdaten',
    path: '/stammdaten/materialien',
    capability: 'web.stammdaten.view',
    inMobileDrawer: true,
  },
  ausschreibungen: {
    key: 'ausschreibungen',
    label: 'Ausschreibungen',
    path: '/ausschreibungen',
    capability: 'web.ausschreibungen.view',
    inMobileDrawer: true,
  },
  templates: { key: 'templates', label: 'Vorlagen', path: '/templates', capability: 'web.vorlagen.view', inMobileDrawer: true },
  employees: { key: 'employees', label: 'Mitarbeiter', path: '/mitarbeiter', capability: 'web.hr.view', inMobileDrawer: true },
  fuhrpark: { key: 'fuhrpark', label: 'Fuhrpark', path: '/fleet', capability: 'web.fuhrpark.view', inMobileDrawer: true },
};

const ORDER: Record<WorkspaceProfile, readonly NavItemKey[]> = {
  voll: [
    'dashboard',
    'chat',
    'inbox',
    'calendar',
    'aufgaben',
    'leads',
    'invoices',
    'contacts',
    'stammdaten',
    'ausschreibungen',
    'templates',
    'employees',
    'fuhrpark',
  ],
  // Angebots-Workflow zuerst; übrige freigeschaltete Bereiche (Zusatzoptionen) danach.
  angebote: [
    'dashboard',
    'new_offer',
    'leads',
    'contacts',
    'stammdaten',
    'templates',
    'chat',
    'inbox',
    'ausschreibungen',
    'calendar',
    'aufgaben',
    'invoices',
    'employees',
    'fuhrpark',
  ],
};

/** Sichtbare Einträge für ein Profil; `canView` prüft das Recht (true ohne Recht). */
export function buildNavItems(profile: WorkspaceProfile, canView: (capability?: string) => boolean): NavItem[] {
  return ORDER[profile].map((key) => ITEMS[key]).filter((item) => canView(item.capability));
}
